import { useState, useEffect } from 'react';
import { checkInstagramVerification } from '../services/firebaseService';

/**
 * Whether the creator's *live* Instagram handle has actually been proven.
 *
 * This used to short-circuit to true for every creator whose application status was not
 * `pendingVerification`, which meant every approved creator rendered as verified whether or
 * not they had ever proven the handle. That was harmless while the handle was read-only; it
 * is not once creators can change it.
 *
 * `instagramVerifiedAt` is the positive signal, set when this creator proves a handle change.
 * Creators who proved their handle at signup predate that field, so their verification record
 * is still consulted — otherwise unlocking the field would mass-downgrade existing creators.
 * Creators with no record at all (imported by staff, or "Verify Later" at signup) come back as
 * `null`: unknown, not unverified, so the profile shows neither badge nor warning for them.
 *
 * A handle change in flight does not affect this: the live handle keeps its own state while
 * the pending handle is shown separately as unproven.
 */
export const useInstagramVerificationStatus = (
  instagramHandle?: string,
  /** Boolean rather than the timestamp itself: realtime snapshots hand back a fresh object
   *  each tick, which would re-run this check on every unrelated profile update. */
  hasVerifiedStamp?: boolean
) => {
  const [isVerified, setIsVerified] = useState<boolean | null>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // A lookup for the previous handle can resolve after a promotion and set isVerified false over a
    // handle that was just proven, flashing "Not verified" on the profile.
    let cancelled = false;

    const checkStatus = async () => {
      if (!instagramHandle) {
        if (!cancelled) setIsVerified(false);
        return;
      }

      if (hasVerifiedStamp) {
        if (!cancelled) {
          setIsVerified(true);
          // Also clears loading: an in-flight lookup may have set it, and this early return skips
          // the finally that would have. Leaving it true suppressed both the verified badge and the
          // unverified prompt in SocialsEdit, so the field showed no state at all.
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const verified = await checkInstagramVerification(instagramHandle);
        if (!cancelled) setIsVerified(verified);
      } catch (err) {
        console.error('Error checking Instagram verification:', err);
        if (!cancelled) {
          setError('Failed to check verification status');
          setIsVerified(false);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    checkStatus();

    return () => {
      cancelled = true;
    };
  }, [instagramHandle, hasVerifiedStamp]);

  return { isVerified, isLoading, error };
};
